import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { FC, useCallback, useState } from "react";
import styled from "styled-components";
import { LegacyImage } from "components";
import { useLockBodyScroll } from "hooks";
import { getImageFormat } from "lib/utils";
import { Features4PartnerContent } from "types/graphql/generated";
import { PartnerModal } from "./PartnerModal";

interface Props {
  className?: string;
  content: Features4PartnerContent;
}

const Partner: FC<Props> = withErrorBoundary(
  ({
    className = "",
    content: {
      logo: {
        data: { logo },
      },
      ...modalContent
    },
  }) => {
    const [isOpenModal, setIsOpenModal] = useState(false);
    const handleOpenModal = useCallback(() => setIsOpenModal(true), []);
    const handleCloseModal = useCallback(() => setIsOpenModal(false), []);

    useLockBodyScroll(isOpenModal);

    return (
      <StyledPartner className={className}>
        <button className="Partner__logoButton" onClick={handleOpenModal} type="button">
          <LegacyImage {...logo} transform={{ format: getImageFormat(logo.ext) }} className="Partner__image" />
        </button>
        <PartnerModal isOpen={isOpenModal} onClose={handleCloseModal} content={modalContent} />
      </StyledPartner>
    );
  },
  {}
);

const StyledPartner = styled.li`
  .Partner__logoButton {
    height: 72px;
    display: flex;
    align-items: center;
    margin-left: 24px;
    margin-top: 24px;
    padding: 0 40px;
    border-radius: 40px;
    box-shadow: 0 1px 0 rgb(25 24 23 / 8%);
    background-color: ${({ theme }) => theme.colors.fog["00"]};
    cursor: pointer;
  }

  .Partner__logoButton:hover {
    background-color: ${({ theme }) => theme.colors.fog["20"]};
    box-shadow: none;
  }

  .Partner__image {
    line-height: 0;
  }
`;

gql`
  fragment Features4PartnerContent on ComponentNestedFeatures4Partners {
    partnerName
    logo {
      data {
        logo: attributes {
          src: hash
          alt: alternativeText
          width
          height
          ext
        }
      }
    }
    ...PartnerModalContent
  }
`;

export { Partner };
